import { useEffect, useMemo, useState } from 'react';
import { STATES } from '../../hooks/useRealtimeCall';
import { formatCost } from '../../utils/voicePricing';
import { MODEL_BY_ID } from '../../lib/aiModels';

// Floating Aurora widget — anchored to the viewport's bottom-start corner so
// the map stays fully interactive (the user can drop a pin / share location
// while talking). No live caption panel: the transcription quality is
// imperfect enough that surfacing it live would confuse rather than help —
// the backend still logs transcripts to consultation_messages for the
// post-call summary; the user reads them in the chat after end-of-call.

function fmtTime(sec, capSec = 600) {
  const mm = String(Math.floor(sec / 60)).padStart(2, '0');
  const ss = String(sec % 60).padStart(2, '0');
  const cmm = String(Math.floor(capSec / 60)).padStart(2, '0');
  const css = String(capSec % 60).padStart(2, '0');
  return `${mm}:${ss} / ${cmm}:${css}`;
}

// ─── Iconography (inline SVG, crisp at every size) ──────────────────────────
const SparkMark = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3.5 13.4 9 19 10.4 13.4 11.8 12 17.3 10.6 11.8 5 10.4 10.6 9 12 3.5Z"
      fill="currentColor"
    />
    <circle cx="19.5" cy="5.5" r="1.2" fill="currentColor" opacity="0.7" />
    <circle cx="5" cy="18.5" r="0.9" fill="currentColor" opacity="0.55" />
  </svg>
);

const MicOn = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="2.5" width="6" height="12" rx="3" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5v3M9 21h6" />
  </svg>
);

const MicOff = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="3" y1="3" x2="21" y2="21" />
    <path d="M9 9.5v2.5a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6" />
    <path d="M17 16.95A7 7 0 0 1 5 12v-1.5M19 12v-.5" />
    <line x1="12" y1="18.5" x2="12" y2="21" />
  </svg>
);

const PhoneDown = () => (
  // Hand-tuned hung-up handset, rotated so it reads as "end call"
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <g transform="rotate(135 12 12)" fill="currentColor">
      <path d="M21.7 13.5a13.6 13.6 0 0 0-19.4 0c-.5.5-.5 1.3 0 1.8l2.6 2.6c.4.4 1.1.5 1.6.2l2.7-1.6c.5-.3.7-.9.6-1.4l-.4-2c2.5-1.1 5.3-1.1 7.8 0l-.4 2c-.1.5.1 1.1.6 1.4l2.7 1.6c.5.3 1.2.2 1.6-.2l2.6-2.6c.5-.5.5-1.3 0-1.8Z"/>
    </g>
  </svg>
);

// ─── Status copy ────────────────────────────────────────────────────────────
function useStatusLabel(state, isMuted) {
  return useMemo(() => {
    switch (state) {
      case STATES.REQUESTING_MIC: return 'در حال درخواست میکروفون…';
      case STATES.CONNECTING:     return 'در حال اتصال…';
      case STATES.ACTIVE:         return isMuted ? 'میکروفون خاموش است' : 'متصل - صحبت کنید';
      case STATES.ENDING:         return 'در حال پایان تماس…';
      case STATES.ERROR:          return 'خطا در ارتباط';
      default:                    return '';
    }
  }, [state, isMuted]);
}

// The cost line names the model that answered (its catalogue label).
function modelLabel(model) {
  return (model && MODEL_BY_ID[model]?.label) || model || '';
}

// ─── Component ──────────────────────────────────────────────────────────────
export default function CallOverlay({ call }) {
  const status = useStatusLabel(call.state, call.isMuted);
  const isActive = call.state === STATES.ACTIVE;
  const isMuted = call.isMuted;
  const isError = call.state === STATES.ERROR;
  const costLine = useMemo(() => {
    if (!isActive) return null;
    const usd  = call.usage && call.usage.cost;
    const text = formatCost(usd);
    if (text === '—') return null;
    return { text, provider: modelLabel(call.model) };
  }, [isActive, call.usage, call.model]);

  // Surface the "primary provider failed — switched to backup operator" toast
  // when call.fellBack flips true (typically right at call-start). The two
  // providers have different voices so being silent about the switch would be
  // confusing. Dismissed automatically after 6 s OR when the user dismisses.
  const [showFellBack, setShowFellBack] = useState(false);
  useEffect(() => {
    if (!call.fellBack) { setShowFellBack(false); return; }
    setShowFellBack(true);
    const t = setTimeout(() => setShowFellBack(false), 6000);
    return () => clearTimeout(t);
  }, [call.fellBack]);

  return (
    <div
      className={`call-widget${isActive ? ' is-active' : ''}${isMuted ? ' is-muted' : ''}${isError ? ' is-error' : ''}`}
      role="status"
      aria-live="polite"
      aria-label="پنل تماس صوتی"
    >
      <div className="call-widget-glow" aria-hidden="true" />
      <div className="call-widget-card">
        <div className="call-widget-header">
          <div className="call-widget-avatar">
            <SparkMark />
            <span className="call-widget-pulse" aria-hidden="true" />
          </div>
          <div className="call-widget-info">
            <span className="call-widget-name">اپراتور هوشمند</span>
            <span className="call-widget-status">{status}</span>
          </div>
          <span className="call-widget-timer" aria-live="polite">
            {fmtTime(call.elapsedSec)}
          </span>
        </div>

        <div className="call-widget-wave" aria-hidden="true">
          <span /><span /><span /><span /><span /><span /><span />
        </div>

        <div className="call-widget-actions">
          <button
            type="button"
            className={`call-widget-mute${isMuted ? ' on' : ''}`}
            onClick={call.toggleMute}
            disabled={!isActive}
            aria-label={isMuted ? 'باز کردن میکروفون' : 'بستن میکروفون'}
            title={isMuted ? 'باز کردن میکروفون' : 'بستن میکروفون'}
          >
            {isMuted ? <MicOff /> : <MicOn />}
          </button>
          <button
            type="button"
            className="call-widget-end"
            onClick={call.endCall}
            aria-label="پایان تماس"
            title="پایان تماس"
          >
            <PhoneDown />
          </button>
        </div>

        {isError && call.error && (
          <div className="call-widget-error" role="alert">{call.error}</div>
        )}

        {showFellBack && (
          <div className="call-widget-fellback" role="status">
            اپراتور پشتیبان فعال شد
          </div>
        )}

        {costLine && (
          <div
            className="call-widget-cost"
            role="status"
            aria-label={`هزینهٔ تقریبی تماس روی ${costLine.provider}: ${costLine.text}`}
            title={`هزینه بر اساس قیمت‌نامهٔ عمومی ${costLine.provider} (به‌جز هزینهٔ احتمالی Grounding گوگل که جداگانه است)`}
          >
            <span className="call-widget-cost-label">هزینهٔ تماس</span>
            <span className="call-widget-cost-value">{costLine.text}</span>
            <span className="call-widget-cost-provider">{costLine.provider}</span>
          </div>
        )}
      </div>
    </div>
  );
}
